<template>
    <table class="table table-striped table-hover table-responsive-md text-center">
        <thead>
            <tr>
                <th>{{ $t("vehicleEntry.carrier") }}</th>
                <th>{{ $t("vehicleEntry.driver_name") }}</th>
                <th>{{ $t("vehicleEntry.licencePlate") }}</th>
                <th>{{ $t("vehicleEntry.from") }}</th>
                <th>{{ $t("vehicleEntry.to") }}</th>
                <th>{{ $t("status.status") }}</th>
                <th></th>
            </tr>
        </thead>
        <tbody v-if="vehicleList.length">
            <tr
                v-for="vehicle in vehicleList"
                :key="vehicle.id"
                class="align-middle"
            >
                <td>{{ vehicle.carrier }}</td>
                <td>{{ vehicle.driver_name }}</td>
                <td>{{ vehicle.licence_plate }}</td>
                <td>
                    <span v-if="vehicle.actual_from">
                        {{ $d(new Date(vehicle.actual_from), 'short') }}
                    </span>
                    <span v-else-if="vehicle.expected_from">
                        {{ $d(new Date(vehicle.expected_from), 'short') }}
                        <span class="fw-bold text-danger">*</span>
                    </span>
                    <span v-else>—</span>
                </td>
                <td>
                    {{ vehicle.actual_to ? $d(new Date(vehicle.actual_to), 'short') : "—" }}
                </td>
                <td>
                    <div
                        v-if="$store.getters.hasGatehousePerm('change_status_vehicleentry') && vehicle.status_name !== 'left'"
                        class="d-inline-flex align-items-center status_hover"
                        style="cursor: pointer;"
                        @click="openStatusModal(vehicle)"
                    >
                        <Icon v-if="vehicle.status_icon" :icon="vehicle.status_icon" class="status_icon me-1" />
                        <span>{{ $t("status.vehicleEntry." + vehicle.status_name) }}</span>
                    </div>
                    <div v-else class="d-inline-flex align-items-center status_hover">
                        <Icon v-if="vehicle.status_icon" :icon="vehicle.status_icon" class="status_icon me-1" />
                        <span>{{ $t("status.vehicleEntry." + vehicle.status_name) }}</span>
                    </div>
                </td>
                <td>
                    <button
                        class="btn btn-primary btn-sm me-2"
                        @click="goToVehicleDetail(vehicle.id)"
                    >
                        <Icon icon="Search"></Icon>
                    </button>

                    <button
                        v-if="vehicle.status_name === 'reserved'"
                        class="btn btn-danger btn-sm"
                        @click="openDeleteModal(vehicle)"
                    >
                        <Icon icon="Trash"></Icon>
                    </button>
                    <button
                        v-else
                        class="invisible"
                        disabled
                    >
                        <Icon icon="Trash"></Icon>
                    </button>
                </td>
            </tr>
        </tbody>

        <tbody v-else>
            <tr>
                <td colspan="7" class="text-center text-muted py-3">
                    {{ $t("vehicleEntry.notFound") }}
                </td>
            </tr>
        </tbody>
    </table>

    <div v-if="totalCount > 0" class="d-flex justify-content-between mx-4">
        <Paginate
            :page-count="totalPages"
            :click-handler="p => $emit('page-change', p)"
            :prev-text="'«'"
            :next-text="'»'"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :page-link-class="'page-link'"
            :active-class="'active'"
            :disabled-class="'disabled'"
        />

        <div class="flex-shrink-0">
            <select
                class="form-select form-select-sm"
                style="height: 40px;"
                :value="perPage"
                @change="$emit('per-page-change', Number($event.target.value))"
            >
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
            </select>
        </div>
    </div>

    <VehicleEntryStatusesModal
        v-if="activeVehicle"
        :vehicleEntry="activeVehicle"
        ref="statusModal"
        @updated="$emit('refresh')"
    />

    <VehicleEntryDeleteModal
        v-if="activeVehicle"
        :vehicleEntry="activeVehicle"
        ref="deleteModal"
        @deleted="$emit('refresh')"
    />
</template>

<script>
import Icon from '@/components/Icon.vue';
import Paginate from 'vuejs-paginate-next';
import VehicleEntryStatusesModal from '@/components/VehicleEntryStatusesModal.vue';
import VehicleEntryDeleteModal from '@/components/VehicleEntryDeleteModal.vue';

export default {
    components: {
        Icon,
        Paginate,
        VehicleEntryStatusesModal,
        VehicleEntryDeleteModal,
    },
    emits: ["page-change", "per-page-change", "refresh"],
    props: {
        items: {
            type: Array,
            required: true,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
        page: {
            type: Number,
            default: 1,
        },
        perPage: {
            type: Number,
            default: 10,
        },
        statuses: {
            type: Array,
            default: null,
        }
    },
    data() {
        return {
            localStatuses: [],
            activeVehicle: null,
        };
    },
    created() {
        if (this.statuses === null) {
            this.getStatuses();
        } else {
            this.localStatuses = this.statuses;
        }
    },
    methods: {
        getStatuses() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/statuses`, {
                params: { model: "vehicleentry" }
            })
            .then(response => {
                this.localStatuses = response.data;
            })
            .catch(error => {
                console.error("Error loading statuses", error);
            });
        },
        goToVehicleDetail(vehicleId) {
            this.$router.push({
                name: "vehicleEntryDetailView",
                params: {
                    slug: this.$route.params.slug,
                    id: vehicleId,
                }
            });
        },
        openDeleteModal(vehicle) {
            this.activeVehicle = vehicle;
            this.$nextTick(() => {
                this.$refs.deleteModal.show();
            });
        },
        openStatusModal(vehicle) {
            this.activeVehicle = vehicle;
            this.$nextTick(() => {
                this.$refs.statusModal.show();
            });
        },
    },
    computed: {
        vehicleList() {
            return this.items;
        },
        totalPages() {
            return Math.ceil(this.totalCount / this.perPage);
        }
    }
}
</script>

<style scoped>
.status_icon {
    width: 25px;
    height: 25px;
}

table {
    table-layout: fixed;
}
</style>